import { Subscribable, } from '@volkswagen-onehub/feature-hub-utils';
import { Layer } from './layer';
export * from './types';
export class LayerManagerV2Impl extends Subscribable {
    constructor(log) {
        super();
        this.log = log;
        this.stack = [];
    }
    openAlert(render, state, options) {
        const newLayer = this.createLayer(render, state, options || {});
        this.addToStack({ layer: newLayer, type: 'ALERT' });
        return newLayer.handle;
    }
    openFocusLayer(render, state, options) {
        const newLayer = this.createLayer(render, state, options);
        this.addToStack({ layer: newLayer, type: 'FOCUS' });
        return newLayer.handle;
    }
    openInteractionLayer(render, state, options) {
        const newLayer = this.createLayer(render, state, options);
        this.addToStack({ layer: newLayer, type: 'INTERACTION' });
        return newLayer.handle;
    }
    openContentLayer(render, state, options) {
        this.log.error('The `openContentLayer` function is deprecated. This delagates `openContentLayerV2` function instead without providing headline and close button label: https://github.com/volkswagen-onehub/components-core/issues/1368');
        return this.openContentLayerV2(render, state, Object.assign(Object.assign({}, options), { contentHeadline: '', closeButtonLabel: '' }));
    }
    openContentLayerV2(render, state, options) {
        const newLayer = this.createLayer(render, state, options);
        this.addToStack({ layer: newLayer, type: 'CONTENTV2' });
        return newLayer.handle;
    }
    openFullscreenLayer(render, state, options) {
        const newLayer = this.createLayer(render, state, options || {});
        this.addToStack({ layer: newLayer, type: 'FULLSCREEN' });
        return newLayer.handle;
    }
    openCustomLayer(render, state, options) {
        const newLayer = this.createLayer(render, state, options);
        this.addToStack({ layer: newLayer, type: 'CUSTOM' });
        return newLayer.handle;
    }
    openDisclaimerLayer(render, state, options = {}) {
        this.closeOpenDisclaimerLayer();
        this.disclaimer = new Layer(render, state, {}, options, () => {
            this.updateInternal('beforeClose');
            if (this.disclaimer) {
                this.disclaimer = undefined;
                return true;
            }
            return false;
        }, () => this.updateInternal('update'));
        this.updateInternal('update');
        return this.disclaimer.handle;
    }
    getDisclaimerLayer() {
        return this.disclaimer
            ? { type: 'DISCLAIMER', layer: this.disclaimer }
            : undefined;
    }
    onRender(id, el) {
        const layers = this.getLayers().filter((layer) => layer.layer.id === id);
        if (el != null && layers.length === 1) {
            layers[0].layer.element = el;
            this.updateInternal('afterRender');
        }
    }
    getLayers() {
        const { stack } = this;
        let alertFound = false;
        const layers = stack
            .slice() // must clone to not modify original array
            .reverse()
            .filter((layer) => {
            if (layer.type === 'ALERT') {
                if (alertFound) {
                    return false;
                }
                alertFound = true;
            }
            return true;
        })
            .reverse();
        return layers;
    }
    closeStackableLayer(layer) {
        const layerIndex = this.stack.findIndex((current) => current.layer === layer);
        if (layerIndex === -1) {
            return false;
        }
        this.stack.splice(layerIndex, 1);
        return true;
    }
    addToStack(layerDescription) {
        this.stack.push(layerDescription);
        this.updateInternal('update');
    }
    createLayer(render, state, options) {
        if (!render) {
            throw new Error('no render function was provided');
        }
        this.closeOpenDisclaimerLayer();
        return new Layer(render, state, {}, options, (currentLayer) => {
            this.updateInternal('beforeClose');
            return this.closeStackableLayer(currentLayer);
        }, () => this.updateInternal('update'));
    }
    closeOpenDisclaimerLayer() {
        if (this.disclaimer && !this.disclaimer.isClosed) {
            this.disclaimer.close();
            this.disclaimer = undefined;
        }
    }
}
export class LayerManagerV2ImplProxy {
    constructor(layerManager) {
        this.layerManager = layerManager;
        this.openLayers = new Set();
        this.subscriptions = new Set();
    }
    unbind() {
        this.openLayers.forEach((handle) => {
            if (!handle.isClosed()) {
                handle.close();
            }
        });
        this.openLayers.clear();
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.clear();
    }
    openAlert(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openAlert(render, state, options));
    }
    openFocusLayer(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openFocusLayer(render, state, options));
    }
    openFullscreenLayer(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openFullscreenLayer(render, state, options));
    }
    openCustomLayer(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openCustomLayer(render, state, options));
    }
    openInteractionLayer(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openInteractionLayer(render, state, options));
    }
    openContentLayer(render, state, options) {
        return this.createProxiedCloseHandle(
        // tslint:disable-next-line: deprecation
        this.layerManager.openContentLayer(render, state, options));
    }
    openContentLayerV2(render, state, options) {
        return this.createProxiedCloseHandle(this.layerManager.openContentLayerV2(render, state, options));
    }
    openDisclaimerLayer(render, state, options = {}) {
        return this.createProxiedCloseHandle(this.layerManager.openDisclaimerLayer(render, state, options));
    }
    onRender(id, el) {
        this.layerManager.onRender(id, el);
    }
    getLayers() {
        return this.layerManager.getLayers();
    }
    getDisclaimerLayer() {
        return this.layerManager.getDisclaimerLayer();
    }
    subscribe(listener) {
        const subscription = this.layerManager.subscribe(listener);
        this.subscriptions.add(subscription);
        return {
            unsubscribe: () => {
                this.subscriptions.delete(subscription);
                subscription.unsubscribe();
            },
        };
    }
    createProxiedCloseHandle(handle) {
        const proxyClose = () => {
            this.openLayers.delete(handle);
            handle.close();
        };
        this.openLayers.add(handle);
        return Object.assign(Object.assign({}, handle), { close: proxyClose });
    }
}
//# sourceMappingURL=index.js.map